import React, { useRef, useState } from 'react';
import { Files, FileText, FileDown, FolderOpen, Hash, Loader2, ScanText, Search, Upload, X } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { checkUploads, MAX_PDF_PAGES, validateFiles } from '@/domain/document-processing/utils/document-validation';

interface NewOrderHomeProps {
  onStart: (files: File[], reference: string) => Promise<void>;
}

export function NewOrderHome({ onStart }: NewOrderHomeProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const submitting = useRef(false);
  const input = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: File[]) => {
    if (submitting.current || !incoming.length) return;
    try {
      const next = [...files, ...incoming];
      if (next.length > 10) throw new Error('Choose up to 10 files for one order. Remove a file before adding more.');
      validateFiles(incoming);
      setFiles(next);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'These files could not be added. Try again.');
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!files.length) {
      setError('Choose at least one document to start verification.');
      input.current?.focus();
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const { rejected } = await checkUploads(files);
      if (rejected.length) {
        setError(rejected.map(({ file, reason }) => `${file.name}: ${reason}`).join(' '));
        return;
      }
      await onStart(files, reference.trim());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create your order. Your selected files are still here. Please try again.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl py-1 sm:py-2" aria-labelledby="new-order-title">
      <div className="mb-4 max-w-2xl">
        <h1 id="new-order-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">Verify a new order</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
          Bring your documents together. Extract names and organisations, then review the findings in one place.
        </p>
      </div>
      <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-8">
        <form onSubmit={submit} className="min-w-0" aria-busy={busy}>
          <fieldset disabled={busy} className="min-w-0 space-y-3">
            <legend className="sr-only">Documents for your new order</legend>
            <div
              onDragOver={event => { event.preventDefault(); if (!busy) setDragging(true); }}
              onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false); }}
              onDrop={event => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files)); }}
              className={`relative !mt-0 rounded-xl border-2 border-dashed px-4 py-4 text-center transition-colors motion-reduce:transition-none sm:px-8 ${dragging ? 'border-primary bg-primary/10' : 'border-primary/30 bg-primary/5'}`}
            >
              <div aria-hidden="true" className="relative mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/15 bg-background text-primary shadow-sm">
                <Files className="h-6 w-6" strokeWidth={1.5} />
                <span className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground ring-4 ring-background">
                  <Upload className="h-4 w-4" />
                </span>
              </div>
              <h2 className="text-lg font-semibold">Drop your order documents here</h2>
              <p className="mb-3 mt-1 text-sm text-muted-foreground">Invoices, purchase orders and supporting documents</p>
              <Label htmlFor="new-order-files" className="sr-only">Choose documents</Label>
              <input ref={input} id="new-order-files" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="peer sr-only"
                onChange={event => { addFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
              <Button type="button" variant="outline" className="bg-background peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2" onClick={() => input.current?.click()}>
                <FolderOpen aria-hidden="true" className="h-4 w-4" />
                Choose documents
              </Button>
              <p className="mt-3 text-xs leading-5 text-muted-foreground">PDF, JPG or PNG. Up to 10 files, 50 MB each.<br />Maximum {MAX_PDF_PAGES} pages per PDF.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-order-reference">Your order reference <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <div className="relative">
                <Hash aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="new-order-reference" className="pl-10" placeholder="e.g. PO-2026-1042" maxLength={200} value={reference} onChange={event => setReference(event.target.value)} />
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <div className="border-t pt-3">
              <Button type="submit" size="lg" className="w-full sm:w-auto">
                {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : <ScanText aria-hidden="true" className="h-4 w-4" />}
                {busy ? 'Starting verification…' : 'Start verification'}
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">Your files will be uploaded when you start verification.</p>
            </div>
            {files.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium" role="status">{files.length} of 10 files selected</p>
                <ul className="divide-y rounded-lg border bg-muted/20 px-3">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="flex min-w-0 items-center gap-3 py-2">
                      <FileText aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1 break-all text-sm">{file.name}</span>
                      <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${file.name}`} onClick={() => { setFiles(previous => previous.filter((_, i) => i !== index)); setError(''); }}>
                        <X aria-hidden="true" className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </fieldset>
        </form>
        <aside className="rounded-xl border border-border bg-muted/30 p-6" aria-labelledby="next-steps-title">
          <h2 id="next-steps-title" className="font-semibold">What happens next</h2>
          <ol className="mt-6">
            {[
              { title: 'Extract', description: 'Read names and organisations from your documents.', Icon: ScanText },
              { title: 'Review', description: 'Inspect the extracted details and any screening findings.', Icon: Search },
              { title: 'Export', description: 'Download your results for your records.', Icon: FileDown },
            ].map(({ title, description, Icon }, index) => (
              <li key={title} className="relative flex gap-3 pb-7 last:pb-0">
                {index < 2 && <span aria-hidden="true" className="absolute bottom-0 left-[19px] top-10 w-px bg-border" />}
                <span aria-hidden="true" className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-background text-primary">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="pt-0.5"><h3 className="text-sm font-semibold">{title}</h3><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</p></div>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </section>
  );
}
